import Master from '@/layout/Master';
import Showcase from '@/layout/Showcase/Showcase';
import { auth, loggedIn } from '@/middleware/auth';
import { routesWithPrefix } from '@/router/handle';
import AwsRouter from '@/router/routes/aws';
import CloudHostingRouter from '@/router/routes/cloud-hosting';
import CloudServerRouter from '@/router/routes/cloud-server';
import DomainRouter from '@/router/routes/domain';
import InvoiceRouter from '@/router/routes/invoice';
import SharedHostingRouter from '@/router/routes/shared-hosting';
import ShowcaseRouter from '@/router/routes/showcase';
import SmsRouter from '@/router/routes/sms';
import SslRouter from '@/router/routes/ssl';
import WalletRouter from '@/router/routes/wallet';
import WPHostingRouter from '@/router/routes/wp-hosting';
import Vue from 'vue';
import VueRouter from 'vue-router';

Vue.use(VueRouter);

const routes = [
    {
        path: '/',
        redirect: { name: 'dashboard' },
    },
    {
        path: '/',
        component: Master,
        children: [
            {
                path: '/dashboard',
                name: 'dashboard',
                component: () => import('@/views/Dashboard/Dashboard'),
                meta: {
                    middleware: [auth],
                },
            },
            ...routesWithPrefix('/domain', 'domain', [auth], DomainRouter),
            ...routesWithPrefix('/invoices', 'invoices', [auth], InvoiceRouter),
            ...routesWithPrefix(
                '/cloud-hosting',
                'cloud-hosting',
                [auth],
                CloudHostingRouter
            ),
            ...routesWithPrefix(
                '/shared-hosting',
                'shared-hosting',
                [auth],
                SharedHostingRouter
            ),
            ...routesWithPrefix(
                '/wp-hosting',
                'wp-hosting',
                [auth],
                WPHostingRouter
            ),
            ...routesWithPrefix(
                '/cloud-server',
                'cloud-server',
                [auth],
                CloudServerRouter
            ),
            ...routesWithPrefix('/wallet', 'wallet', [auth], WalletRouter),
            ...routesWithPrefix('/sms', 'sms', [auth], SmsRouter),
            {
                path: '/api',
                name: 'api',
                component: () => import('@/views/APIKey/APIKey'),
                meta: {
                    middleware: [auth],
                },
            },
            {
                path: '/profile',
                name: 'profile',
                component: () => import('@/views/MyProfile/MyProfile'),
                meta: {
                    middleware: [auth],
                },
            },
            {
                path: '/notification',
                name: 'notification',
                component: () => import('@/views/Notifications/Notification'),
                meta: {
                    middleware: [auth],
                },
            },
            ...routesWithPrefix('/aws', 'aws', [auth], AwsRouter),
            {
                path: '/promo-credit',
                name: 'promo-credit',
                component: () => import('@/views/PromoCredit/PromoCredit'),
                meta: {
                    middleware: [auth],
                },
            },
            {
                path: '/affiliate',
                name: 'affiliate',
                component: () => import('@/views/Affiliate/Affiliate'),
                meta: {
                    middleware: [auth],
                },
            },
            {
                path: '/loyalty-program',
                name: 'loyalty-program',
                component: () =>
                    import('@/views/LoyaltyProgram/LoyaltyProgram'),
                meta: {
                    middleware: [auth],
                },
            },
            ...routesWithPrefix('/ssl', 'ssl', [auth], SslRouter),
            {
                path: '/activity-log',
                name: 'activity-logs',
                component: () => import('@/views/ActivityLogs/ActivityLogs'),
                meta: {
                    middleware: [auth],
                },
            },
        ],
    },
    ...(process.env.NODE_ENV === 'development'
        ? [
              {
                  path: '/',
                  component: Showcase,
                  children: [
                      ...routesWithPrefix(
                          '/showcase',
                          'showcase',
                          [],
                          ShowcaseRouter
                      ),
                  ],
              },
          ]
        : []),
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/Login/Login'),
        meta: {
            middleware: [loggedIn],
        },
    },
    {
        path: '/vm-console/:id',
        name: 'vm-console',
        component: () => import('@/views/VmConsole/VmConsole'),
        meta: {
            middleware: [auth],
        },
    },
    {
        path: '/404',
        name: 'not-found',
        component: () => import('@/views/Errors/404/404'),
    },
    {
        path: '/maintenance',
        name: 'maintenance',
        component: () => import('@/views/Maintenance/Maintenance'),
    },
    {
        path: '*',
        redirect: { name: 'dashboard' },
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior: async function () {
        return { x: 0, y: 0 };
    },
});

const offPage = process?.env?.VUE_APP_MAINTENANCE?.toLowerCase() === 'true';

router.beforeEach(async (to, from, next) => {
    if (offPage) {
        return next({
            name: 'maintenance',
        });
    }
    const middlewares = to.meta.middleware;
    if (middlewares) {
        const context = {
            to,
            from,
            next,
        };
        for (let middleware of middlewares) {
            const result = await middleware(context);
            if (!result.valid) {
                next(result.redirect);
                break;
            }
        }
    }
    return next();
});

export default router;
